import { useCallback, useEffect, useRef, useState } from 'react';

import { ApolloError } from '@apollo/client';

import { useAlert } from '@spotify/confidence-core-react';
import {
  type ChatEventFragment,
  getUserFacingErrorMessage,
} from '@spotify/confidence-graphql';

import { ChatAttachment, attachmentKey, splitAttachments } from '../../types';
import {
  buildUserMessage,
  resolveAttachmentsWithFallback,
} from '../attachmentMessage';
import { useChatImageUpload } from '../useChatImageUpload';
import { useResolveAttachments } from '../useResolveAttachments';
import { useSendUserMessage } from '../useSendUserMessage';

const DRAFT_KEY_PREFIX = 'chat-draft';

const getDraftKey = (chatName: string): string =>
  `${DRAFT_KEY_PREFIX}-${chatName}`;

const getDraft = (chatName: string): string => {
  try {
    return localStorage.getItem(getDraftKey(chatName)) ?? '';
  } catch {
    return '';
  }
};

const saveDraft = (chatName: string, value: string): void => {
  try {
    if (value) {
      localStorage.setItem(getDraftKey(chatName), value);
    } else {
      localStorage.removeItem(getDraftKey(chatName));
    }
  } catch {
    // localStorage unavailable
  }
};

const CHAT_EVENT_ID_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';
const CHAT_EVENT_ID_LENGTH = 20;
const NETWORK_RETRY_DELAY_MS = 1_000;

const newChatEventId = (): string => {
  const bytes = crypto.getRandomValues(new Uint8Array(CHAT_EVENT_ID_LENGTH));
  return Array.from(
    bytes,
    byte => CHAT_EVENT_ID_ALPHABET[byte % CHAT_EVENT_ID_ALPHABET.length],
  ).join('');
};

const isNetworkError = (error: unknown): boolean =>
  error instanceof TypeError ||
  (error instanceof ApolloError && Boolean(error.networkError));

type PendingSubmission = {
  chatName: string;
  fingerprint: string;
  chatEventId: string;
};

type ChatSubmitOptions = {
  message?: string;
};

type ChatSubmitCallbacks = {
  onStart?: (content: string, attachments: ChatAttachment[]) => void;
  onSuccess?: (event: ChatEventFragment) => void;
  onFailure?: () => void;
};

export type UseChatSubmitResult = {
  inputValue: string;
  isSending: boolean;
  attachments: ChatAttachment[];
  isUploading: boolean;
  handleInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleSubmit: (options?: ChatSubmitOptions) => Promise<boolean>;
  clearInput: () => void;
  addAttachment: (attachment: ChatAttachment) => void;
  removeAttachment: (key: string) => void;
  uploadImage: (file: File) => void;
};

export const useChatSubmit = (
  chatName: string,
  callbacks: ChatSubmitCallbacks = {},
): UseChatSubmitResult => {
  const initialDraft = getDraft(chatName);
  const [inputValue, setInputValue] = useState(initialDraft);
  const inputValueRef = useRef(initialDraft);

  useEffect(() => {
    return () => {
      saveDraft(chatName, inputValueRef.current);
    };
  }, [chatName]);

  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { sendUserMessage } = useSendUserMessage();
  const { resolveAttachments } = useResolveAttachments();
  const alertApi = useAlert();

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const nextValue = e.target.value;
      if (nextValue === inputValueRef.current) {
        return;
      }

      inputValueRef.current = nextValue;
      setInputValue(nextValue);
      saveDraft(chatName, nextValue);
    },
    [chatName],
  );

  const addAttachment = useCallback((attachment: ChatAttachment) => {
    const key = attachmentKey(attachment);
    setAttachments(current =>
      current.some(a => attachmentKey(a) === key)
        ? current
        : [...current, attachment],
    );
  }, []);

  const removeAttachment = useCallback((key: string) => {
    setAttachments(current => current.filter(a => attachmentKey(a) !== key));
  }, []);

  const clearInput = useCallback((): void => {
    setInputValue('');
    inputValueRef.current = '';
    saveDraft(chatName, '');
  }, [chatName]);

  const { uploadImage, isUploading, removeImageAttachment, warnIfUploading } =
    useChatImageUpload({
      attachments,
      addAttachment,
      removeAttachment,
    });

  const isSubmittingRef = useRef(false);
  const pendingSubmissionRef = useRef<PendingSubmission | null>(null);

  const handleSubmit = useCallback(
    async (submitOptions?: ChatSubmitOptions) => {
      const content = (submitOptions?.message ?? inputValueRef.current).trim();
      if ((!content && attachments.length === 0) || isSubmittingRef.current) {
        return false;
      }

      if (warnIfUploading()) return false;

      isSubmittingRef.current = true;
      setIsSubmitting(true);
      const fingerprint = JSON.stringify([
        content,
        attachments.map(attachmentKey).sort(),
      ]);
      const pending = pendingSubmissionRef.current;
      const chatEventId =
        pending?.chatName === chatName && pending.fingerprint === fingerprint
          ? pending.chatEventId
          : newChatEventId();
      pendingSubmissionRef.current = { chatName, fingerprint, chatEventId };
      const originalDraft = inputValueRef.current;
      const submittedKeys = new Set(attachments.map(attachmentKey));
      callbacks.onStart?.(content, attachments);
      clearInput();
      setAttachments(current =>
        current.filter(a => !submittedKeys.has(attachmentKey(a))),
      );

      try {
        const { imageUris, docAttachments } = splitAttachments(attachments);

        const { messageContent, sentAttachments } =
          await resolveAttachmentsWithFallback(
            content,
            chatName,
            docAttachments,
            resolveAttachments,
          );

        const userMessage = buildUserMessage(
          messageContent,
          sentAttachments,
          imageUris,
        );
        let event: ChatEventFragment;
        try {
          event = await sendUserMessage(chatName, userMessage, chatEventId);
        } catch (error: unknown) {
          if (!isNetworkError(error)) throw error;
          await new Promise(resolve =>
            setTimeout(resolve, NETWORK_RETRY_DELAY_MS),
          );
          event = await sendUserMessage(chatName, userMessage, chatEventId);
        }

        pendingSubmissionRef.current = null;
        callbacks.onSuccess?.(event);
        return true;
      } catch (error: unknown) {
        const submittedDraft = submitOptions?.message ? content : originalDraft;
        const restoredDraft = inputValueRef.current
          ? [submittedDraft, inputValueRef.current].filter(Boolean).join('\n\n')
          : submittedDraft;
        inputValueRef.current = restoredDraft;
        setInputValue(restoredDraft);
        saveDraft(chatName, restoredDraft);
        setAttachments(current => [
          ...attachments.filter(
            submitted =>
              !current.some(
                existing =>
                  attachmentKey(existing) === attachmentKey(submitted),
              ),
          ),
          ...current,
        ]);
        callbacks.onFailure?.();
        alertApi.post({
          severity: 'error',
          message: getUserFacingErrorMessage(
            error,
            "Couldn't send message. Please try again.",
          ),
        });
        return false;
      } finally {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }
    },
    [
      alertApi,
      attachments,
      callbacks,
      chatName,
      clearInput,
      resolveAttachments,
      sendUserMessage,
      warnIfUploading,
    ],
  );

  return {
    inputValue,
    isSending: isSubmitting,
    attachments,
    isUploading,
    handleInputChange,
    handleSubmit,
    clearInput,
    addAttachment,
    removeAttachment: removeImageAttachment,
    uploadImage,
  };
};
